import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useGentleMotion as useReducedMotion } from '../hooks/useGentleMotion';
import { Reveal } from './Reveal';
import { ConfettiRain } from './ConfettiRain';
import { bonuses } from '../data/bonuses';
import { useMarket } from '../contexts/MarketContext';
import { estimatedInMarket } from '../utils/money';
const CONFETTI_COLORS = ['#F6C453', '#F27C70', '#6CBBD5', '#2D8C5A', '#F3A340'];

/** Short, one-time confetti burst (1–2s) when the bonuses enter the viewport. */
function ConfettiBurst({
  active
}: {
  active: boolean;
}) {
  const reduce = useReducedMotion();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!active || reduce) return;
    setVisible(true);
    const t = window.setTimeout(() => setVisible(false), 2200);
    return () => window.clearTimeout(t);
  }, [active, reduce]);
  if (!visible) return null;
  return <div className="pointer-events-none absolute inset-x-0 top-0 h-72 overflow-hidden" aria-hidden="true" data-id="element-67" data-mp="1r8tf0e9o8jhn">
      {Array.from({
      length: 30
    }).map((_, i) => <motion.span key={i} className="absolute h-2 w-3.5 rounded-full" style={{
      left: `${(i * 3.3 + 3) % 97}%`,
      backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
    }} initial={{
      y: -30,
      opacity: 0,
      rotate: 0
    }} animate={{
      y: 240 + i % 5 * 24,
      opacity: [0, 1, 0],
      rotate: 260
    }} transition={{
      duration: 1.8,
      delay: i % 9 * 0.07,
      ease: 'easeIn'
    }} data-id="element-68" data-mp="3deaepkcm6eh6" data-mpi={i} />)}
    </div>;
}
export function Bonuses() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  const reduce = useReducedMotion();
  const {
    market
  } = useMarket();
  return <section ref={ref} className="relative w-full overflow-hidden bg-cream py-16 sm:py-20" aria-labelledby="bonuses-title" data-id="element-69" data-mp="noq7t0gefx1u">
      <ConfettiRain count={14} opacity={0.5} data-id="element-70" data-mp="itaz0mx90rca" />
      <ConfettiBurst active={inView} data-id="element-71" data-mp="3chv5zseabgnp" />

      <div className="relative mx-auto max-w-content px-5 sm:px-8" data-id="element-72" data-mp="3cl0w9ayngol0">
        <Reveal data-id="element-73" data-mp="3hgolfqd4thqi">
          <p className="text-center font-display text-[11px] font-extrabold uppercase tracking-[0.2em] text-tangerine" data-id="element-74" data-mp="3hg6fu34kxr5x">
            Incluido hoy
          </p>
          <h2 id="bonuses-title" className="mx-auto mt-3 max-w-2xl text-center font-display text-2xl font-extrabold uppercase leading-tight text-navy sm:text-4xl" data-id="element-75" data-mp="3qixt88h0mx07">
            Tu compra incluye <span className="text-brandgreen" data-id="element-76" data-mp="2xutl7gaz27uc">10 bonos gratis</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center font-sans text-sm leading-relaxed text-muted sm:text-base" data-id="element-77" data-mp="32sv5g4k4wd7e">
            Recursos extra para organizar y aprovechar las actividades.
          </p>
        </Reveal>

        <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-78" data-mp="2a3wjewcw4qwt">
          {bonuses.map((b, i) => <Reveal as="li" key={b.n} delay={Math.min(i * 0.06, 0.5)} data-id="element-79" data-mp="2f1fujneaknkj" data-mpi={i}>
              <article className={['flex h-full flex-col rounded-3xl border bg-white p-5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-card motion-reduce:transition-none motion-reduce:hover:translate-y-0', b.featured ? 'border-sun/70 shadow-soft' : 'border-navy/10'].join(' ')} data-id="element-80" data-mp="27iou9rdjqxkt">
                <div className="flex items-center justify-between" data-id="element-81" data-mp="1om72xb2m0zp3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy font-display text-[11px] font-extrabold text-white" data-id="element-82" data-mp="1jup34qcbp55p">
                    {b.n}
                  </span>
                  <span className="rounded-full bg-brandgreen px-2.5 py-1 font-display text-[10px] font-extrabold uppercase tracking-wide text-white" data-id="element-83" data-mp="19wj36ea2u93h">
                    Gratis
                  </span>
                </div>

                {/* Pastel plate matched to the cover; the mockup reads as a clean cut-out. */}
                <div className="mt-4 flex items-end justify-center overflow-hidden rounded-2xl px-4 pt-5" style={{
              backgroundColor: b.bg
            }} data-id="element-84" data-mp="30xkf9kyercsv">
                  <motion.img src={b.image} alt={`Mockup del bono ${b.n}: ${b.title}`} width={1024} height={1024} decoding="async" className={['cutout w-auto object-contain', b.featured ? 'h-52 sm:h-60' : 'h-44 sm:h-48'].join(' ')} animate={reduce ? undefined : {
                y: [0, -7, 0],
                rotate: [-1, 1.5, -1]
              }} transition={{
                duration: 6 + i % 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.2
              }} loading="lazy" data-id="element-85" data-mp="2r8ssdr87fiot" />
                </div>

                <h3 className="mt-5 font-display text-base font-extrabold uppercase leading-snug text-navy" data-id="element-86" data-mp="llv38ib9zoi5">
                  {b.title}
                </h3>
                <p className="mt-1.5 flex-1 font-sans text-sm leading-relaxed text-muted" data-id="element-87" data-mp="ud3p4tg59azq">
                  {b.description}
                </p>

                <div className="mt-4 flex items-center justify-between border-t border-navy/10 pt-3" data-id="element-88" data-mp="plc5z3zbi7jm">
                  <span className="font-sans text-[11px] uppercase tracking-wide text-muted" data-id="element-89" data-mp="149zzz5tim97g">
                    Valor estimado
                  </span>
                  <span className="font-display text-sm font-extrabold text-muted line-through decoration-offer decoration-2" data-id="element-90" data-mp="1dbzg7vwph99c">
                    {estimatedInMarket(b.valueUsd, market)}
                  </span>
                </div>
              </article>
            </Reveal>)}
        </ul>
      </div>
    </section>;
}